/**
 * 拓客工作台(启客嵌入,1025)。后端用登录人手机号向启客换一条免登链接,前端直接塞 iframe。
 * 链接里带一次性登录凭据 —— 每次进入现换,不缓存。
 */
import { useQuery } from "@tanstack/react-query";

import { api } from "@/shared/api";

const BASE = "/acq/qike";

export interface QikeStatus {
  /** 平台配了启客 AK/SK;false = 页面显示「未开通」、二级入口不出。 */
  enabled: boolean;
  modules: string[];
}

/** 方案 moduleName → 页签名。顺序即页签顺序;后端 MODULES 之外的不出。 */
export const QIKE_MODULE_LABELS: Record<string, string> = {
  searchHome: "企业探查",
  filter: "高级筛选",
  map: "地图拓客",
  relation: "关系拓客",
  market: "潜客市场",
  subscribe: "潜客订阅",
  batch: "批量查询",
  unlockRecord: "解锁记录",
};

export const qikeService = {
  status: () => api.get<QikeStatus>(`${BASE}/status`),
  link: (module: string, mobile: boolean) =>
    api.post<{ url: string }>(`${BASE}/link`, { module, mobile }),
};

let cached: Promise<boolean> | null = null;

/** 二级入口用:只问一次;失败当没开通(不因为这个口挂了把整个舱外壳弄坏)。 */
export function qikeEnabledOnce(): Promise<boolean> {
  if (!cached) {
    cached = qikeService
      .status()
      .then((s) => Boolean(s?.enabled))
      .catch(() => false);
  }
  return cached;
}

/**
 * 卡墙用:启客开没开(1028 补卡)。失败 / 未回来一律 false —— 宁可不显示,也不给一张点进去「未开通」的死卡。
 * 与 qikeEnabledOnce 的区别:那个是二级入口的一次性探测,这个是给 useClusterCards 的响应式判据(共享缓存)。
 */
export function useQikeEnabled(): boolean {
  const { data } = useQuery<QikeStatus>({
    queryKey: ["acq", "qike", "status"],
    queryFn: qikeService.status,
    staleTime: 5 * 60_000,
    retry: false,
    // 1102:失败本来就当「没开通」静默处理,不许再走全局兜底 toast —— 非租户角色(平台 admin 等)
    // 拿 403,原来每次强刷都弹「操作没有成功 · 排查号…」。
    meta: { suppressGlobalErrorToast: true },
  });
  return Boolean(data?.enabled);
}

export function __resetQikeEnabledCache() {
  cached = null;
}
